import { Injectable } from "@angular/core";
import { ActivityType, SystemActivity } from "../../models/home";
import { LanguageService } from "../language/language.service";

export type HomeActivityImportance = "decision" | "support";

export interface HomeActivityDisplay {
  title: string;
  summary: string;
  impactSummary: string;
  documentsSummary: string | null;
  signalLabel: string | null;
  importance: HomeActivityImportance;
}

@Injectable({ providedIn: "root" })
export class HomeActivityDisplayUxService {
  constructor(private readonly lang: LanguageService) {}

  getDisplay(activity: SystemActivity): HomeActivityDisplay {
    const importance = this.getImportance(activity);
    const eventKey = this.getEventKey(activity, importance);

    return {
      title: this.lang.translate(`home.lastActivity.event.${eventKey}`),
      summary: this.lang.translate(`home.lastActivity.event.${eventKey}Copy`),
      impactSummary: this.getImpactSummary(activity, importance),
      documentsSummary: this.getDocumentsSummary(activity),
      signalLabel: importance === "support" ? this.lang.translate("home.lastActivity.supportSignal") : null,
      importance
    };
  }

  private getImportance(activity: SystemActivity): HomeActivityImportance {
    if (activity.impact.kind === "priority_shift") {
      return "decision";
    }

    if (activity.impact.summary && !this.isGenericSupportSummary(activity.impact.summary)) {
      return "decision";
    }

    if ((activity.documentsAffected ?? 0) > 0 || activity.documentsExamples.length > 0) {
      return "decision";
    }

    return "support";
  }

  private getEventKey(activity: SystemActivity, importance: HomeActivityImportance): string {
    const haystack = `${activity.description} ${activity.impact.summary}`.toLowerCase();

    if (/(missing|falt|blocked|bloque)/.test(haystack)) {
      return "missingInformationDetected";
    }

    if (activity.impact.kind === "priority_shift") {
      return "priorityRecalculated";
    }

    if (/(state|status|moved|changed|unblocked|desbloq)/.test(haystack)) {
      return "stateChanged";
    }

    if (this.isActionEvent(activity.type, haystack)) {
      return "actionExecuted";
    }

    if (activity.impact.kind === "new_date" || this.isEvidenceEvent(activity.type, haystack)) {
      return "evidenceAdded";
    }

    if (importance === "support") {
      return "supportSignal";
    }

    return "decisionUpdated";
  }

  private getImpactSummary(activity: SystemActivity, importance: HomeActivityImportance): string {
    if (importance === "support") {
      return this.lang.translate("home.lastActivity.event.supportSignalCopy");
    }

    const summary = this.normalizeCopy(activity.impact.summary) ?? this.normalizeCopy(activity.description);
    if (!summary || this.isGenericSupportSummary(summary)) {
      return this.lang.translate("home.lastActivity.event.decisionUpdatedCopy");
    }

    return summary;
  }

  private getDocumentsSummary(activity: SystemActivity): string | null {
    if (activity.documentsExamples.length > 0) {
      return activity.documentsExamples.join(", ");
    }

    if ((activity.documentsAffected ?? 0) > 0) {
      return `${activity.documentsAffected}`;
    }

    return null;
  }

  private isActionEvent(type: ActivityType, haystack: string): boolean {
    return type === "validation" || /(execut|queued|completed|confirm|applied|procesad)/.test(haystack);
  }

  private isEvidenceEvent(type: ActivityType, haystack: string): boolean {
    return type === "ingestion" || type === "extraction" || /(evidence|import|date|ingest|extract|adjunt)/.test(haystack);
  }

  private normalizeCopy(value?: string | null): string | null {
    if (!value) {
      return null;
    }

    const cleaned = value.replace(/\s+/g, " ").trim();
    if (!cleaned) {
      return null;
    }

    const firstSentence = cleaned.split(/(?<=[.!?])\s+/)[0]?.trim() ?? cleaned;
    return /[.!?]$/.test(firstSentence) ? firstSentence : `${firstSentence}.`;
  }

  private isGenericSupportSummary(value: string): boolean {
    const normalized = value.toLowerCase();
    return /(clasificaci[oó]n detectada|priority billing action|policy engine|impacto en la cola|documents impacted 0|documentos impactados 0)/.test(normalized);
  }
}
